/**
 * Property Mapper Utility
 * 
 * Handles the migration from snake_case to camelCase properties.
 * This utility provides backward compatibility during the API migration period.
 * 
 * Usage:
 * - Use normalizeApiResponse() for all API responses
 * - Use denormalizeForApi() when sending data to API (if needed)
 * 
 * Once migration is complete, this file can be removed.
 */

// Mapping of old snake_case properties to new camelCase properties
const PROPERTY_MAP: Record<string, string> = {
    // Contact properties
    'first_name': 'firstName',
    'last_name': 'lastName',
    'mobile_no': 'mobileNo',
    'pet_list': 'petList',
    
    // Zone properties
    'zone_name': 'zoneName',
    'zone_type': 'zoneType',
    'zone_coordinates': 'zoneCoordinates',
    
    // Subscription/Plan properties
    'plan_amount': 'planAmount',
    'plan_type': 'planType',
    'plan_start': 'planStart',
    'plan_end': 'planEnd',
    'plan_status': 'planStatus',
    'device_id': 'deviceId',
    'device_name': 'deviceName',
    'billing_info': 'billingInfo',
    'account_id': 'accountId',
    
    // Billing/Address properties
    'street_addr': 'streetAddr',
    'zip_code': 'zipCode',
    'postal_code': 'postalCode',
    'usa_city': 'usaCity',
    'canada_city': 'canadaCity',
    'address1': 'address1',
    'address2': 'address2',
    'address3': 'address3',
    
    // Timezone properties
    'timezone_name': 'timezoneName',
    
    // Add more mappings as discovered
}

// Reverse mapping for sending data back to API (if needed during transition)
const REVERSE_PROPERTY_MAP: Record<string, string> = Object.entries(PROPERTY_MAP).reduce(
    (acc, [oldKey, newKey]) => {
        acc[newKey] = oldKey
        return acc
    },
    {} as Record<string, string>
)

/**
 * Recursively transforms object properties from snake_case to camelCase
 * Handles nested objects and arrays
 */
export function normalizeApiResponse<T = any>(data: any): T {
    if (data === null || data === undefined) {
        return data
    }

    // Handle arrays
    if (Array.isArray(data)) {
        return data.map(item => normalizeApiResponse(item)) as T
    }

    // Handle objects
    if (typeof data === 'object') {
        const normalized: any = {}
        
        for (const [key, value] of Object.entries(data)) {
            // Use mapped property name if it exists, otherwise keep original
            const newKey = PROPERTY_MAP[key] || key
            
            // Recursively normalize nested objects/arrays
            normalized[newKey] = normalizeApiResponse(value)
        }
        
        return normalized as T
    }

    // Return primitives as-is
    return data
}

/**
 * Transforms object properties from camelCase back to snake_case
 * Use this when sending data to API during transition period
 */
export function denormalizeForApi<T = any>(data: any): T {
    if (data === null || data === undefined) {
        return data
    }

    // Handle arrays
    if (Array.isArray(data)) {
        return data.map(item => denormalizeForApi(item)) as T
    }

    // Handle objects
    if (typeof data === 'object') {
        const denormalized: any = {}
        
        for (const [key, value] of Object.entries(data)) {
            // Use reverse mapped property name if it exists, otherwise keep original
            const oldKey = REVERSE_PROPERTY_MAP[key] || key
            
            // Recursively denormalize nested objects/arrays
            denormalized[oldKey] = denormalizeForApi(value)
        }
        
        return denormalized as T
    }

    // Return primitives as-is
    return data
}

/**
 * Helper to check if data is already normalized (has camelCase properties)
 * Useful for handling mixed responses during transition
 */
export function isNormalized(data: any): boolean {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return true
    }

    // Check if any snake_case properties exist
    const keys = Object.keys(data)
    return !keys.some(key => key.includes('_') && PROPERTY_MAP[key])
}

/**
 * Smart normalize - only transforms if data contains snake_case properties
 * Useful for handling responses that might already be normalized
 */
export function smartNormalize<T = any>(data: any): T {
    if (isNormalized(data)) {
        return data
    }
    return normalizeApiResponse<T>(data)
}
